/**
 * Company pages static data — Brand / Careers / Investors.
 *
 * Copy lives in langs/<locale>/company.json and is switched with next-intl.
 */

// ── Brand SVG paths ────────────────────────────────────────────────

/** Full DIGEN wordmark path (viewBox 0 0 76 20) */
export const WORDMARK_PATH =
  'M65.59.278h4.164l3.229 11.639h.06V.277H76v19.445h-3.41L68.609 5.528h-.06v14.194H65.59zM54.63.278h9.052v2.778h-5.733v5.139h4.556v2.777h-4.556v5.973h5.733v2.777H54.63zM47.551 20q-2.414 0-3.68-1.25-1.269-1.278-1.268-3.639V4.89q0-2.361 1.267-3.611Q45.137 0 47.551 0t3.681 1.278q1.267 1.25 1.267 3.61v1.668h-3.138V4.694q0-1.916-1.72-1.916t-1.72 1.916v10.64q0 1.888 1.72 1.888t1.72-1.889v-3.805h-1.659V8.75h4.797v6.361q0 2.361-1.267 3.639Q49.964 20 47.552 20M37.04.278h3.319v19.444h-3.32zM24.783.278h5.069q2.474 0 3.71 1.222Q34.8 2.723 34.8 5.083v9.834q0 2.36-1.237 3.583-1.237 1.222-3.712 1.222h-5.068zm5.008 16.667q.815 0 1.237-.445.453-.445.453-1.444V4.945q0-1-.453-1.445-.422-.444-1.237-.444h-1.69v13.889zM13.698 10c0-2.92-1.385-4.22-2.636-4.904-1.462-.8-3.286-.78-5.901-.78H2c-.943 0-1.414 0-1.707-.293S0 3.259 0 2.316v-1.17C0 .609 0 .339.167.172.335.004.604.004 1.143.004H5.16c2.187 0 5.398-.143 8.112 1.342 2.452 1.342 4.9 3.974 4.9 8.654s-2.448 7.312-4.9 8.654c-2.714 1.485-5.925 1.342-8.112 1.342H1.143c-.539 0-.808 0-.976-.167S0 19.392 0 18.853v-1.169c0-.943 0-1.414.293-1.707s.764-.293 1.707-.293h3.16c2.616 0 4.44.02 5.902-.78 1.252-.684 2.636-1.985 2.636-4.904'

/** DIGEN glyph "D" path (viewBox 0 0 19 20) */
export const GLYPH_PATH =
  'M13.698 10c0-2.92-1.385-4.22-2.636-4.904-1.462-.8-3.286-.78-5.901-.78H2c-.943 0-1.414 0-1.707-.293S0 3.259 0 2.316v-1.17C0 .609 0 .339.167.172.335.004.604.004 1.143.004H5.16c2.187 0 5.398-.143 8.112 1.342 2.452 1.342 4.9 3.974 4.9 8.654s-2.448 7.312-4.9 8.654c-2.714 1.485-5.925 1.342-8.112 1.342H1.143c-.539 0-.808 0-.976-.167S0 19.392 0 18.853v-1.169c0-.943 0-1.414.293-1.707s.764-.293 1.707-.293h3.16c2.616 0 4.44.02 5.902-.78 1.252-.684 2.636-1.985 2.636-4.904'

// ── Brand tones ────────────────────────────────────────────────────

export interface BrandTone {
  id: 'gold' | 'flare' | 'ink' | 'cloud'
  hex: string
}

export const TONES: BrandTone[] = [
  { id: 'gold', hex: '#d4b45a' },
  { id: 'flare', hex: '#ebe9f7' },
  { id: 'ink', hex: '#121214' },
  { id: 'cloud', hex: '#f4f0ea' }
]

/** Solid background colors for each tone (used when surface = solid) */
export const SOLID_BG: Record<BrandTone['id'], string> = {
  gold: '#2c2618',
  flare: '#1b1b1f',
  ink: '#efebe3',
  cloud: '#232228'
}

// ── Perks ──────────────────────────────────────────────────────────

export type PerkIcon = 'spark' | 'globe' | 'dollar'

export interface Perk {
  icon: PerkIcon
  /** i18n key suffix for title, e.g. "b2t" -> company.careers.b2t */
  titleKey: string
  /** i18n key suffix for description, e.g. "b2d" -> company.careers.b2d */
  descKey: string
}

export const PERKS: Perk[] = [
  { icon: 'spark', titleKey: 'b2t', descKey: 'b2d' },
  { icon: 'globe', titleKey: 'b1t', descKey: 'b1d' },
  { icon: 'dollar', titleKey: 'b3t', descKey: 'b3d' }
]

// ── Skill cloud (Careers "Why DIGEN" visual) ───────────────────────

export interface SkillCloudItem {
  nameKey: string
  kind: 'core' | 'sat'
  /** CSS ring class suffix for core items: r1 / r2 / r3 / r4 */
  ring?: string
  x: string
  y: string
}

export const SKILL_CLOUD: SkillCloudItem[] = [
  { nameKey: 'movie', kind: 'core', ring: 'r1', x: '50%', y: '48%' },
  { nameKey: 'music', kind: 'core', ring: 'r2', x: '72%', y: '38%' },
  { nameKey: 'drama', kind: 'core', ring: 'r3', x: '30%', y: '62%' },
  { nameKey: 'image', kind: 'core', ring: 'r4', x: '68%', y: '66%' },
  { nameKey: 'cinematic', kind: 'sat', x: '36%', y: '22%' },
  { nameKey: 'camera', kind: 'sat', x: '58%', y: '20%' },
  { nameKey: 'cuts', kind: 'sat', x: '22%', y: '38%' },
  { nameKey: 'lipSync', kind: 'sat', x: '14%', y: '54%' },
  { nameKey: 'score', kind: 'sat', x: '86%', y: '24%' },
  { nameKey: 'lighting', kind: 'sat', x: '88%', y: '50%' },
  { nameKey: 'captions', kind: 'sat', x: '84%', y: '64%' },
  { nameKey: 'character', kind: 'sat', x: '16%', y: '74%' },
  { nameKey: 'storyboard', kind: 'sat', x: '34%', y: '86%' },
  { nameKey: 'motion', kind: 'sat', x: '52%', y: '86%' },
  { nameKey: 'product', kind: 'sat', x: '80%', y: '80%' },
  { nameKey: 'ads', kind: 'sat', x: '64%', y: '88%' },
  { nameKey: 'aerial', kind: 'sat', x: '90%', y: '36%' }
]

export type DeptId = 'engineering' | 'product' | 'creative' | 'growth'

export const DEPT_IDS: DeptId[] = ['engineering', 'product', 'creative', 'growth']

export interface Role {
  id: string
  deptId: DeptId
}

export const ROLES: Role[] = [
  { id: 'fs', deptId: 'engineering' },
  { id: 'sre', deptId: 'engineering' },
  { id: 'sea', deptId: 'engineering' },
  { id: 'fde', deptId: 'engineering' },
  { id: 'pm', deptId: 'product' },
  { id: 'gpm', deptId: 'product' },
  { id: 'vd', deptId: 'product' },
  { id: 'dir', deptId: 'creative' },
  { id: 'mo', deptId: 'creative' },
  { id: 'gm', deptId: 'growth' },
  { id: 'gc', deptId: 'growth' },
  { id: 'cl', deptId: 'growth' }
]

